'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Check } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';

export default function Newsletter({
  heading = 'Get the Kettura Journal',
  subheading = 'Seasonal guides, cabin availability & insider Lapland tips',
  emailLabel = 'Your email address',
  subscribeLabel = 'Subscribe',
  subscribingLabel = 'Subscribing...',
  successMessage = 'Thanks for subscribing! Check your inbox soon.',
  errorFallback = 'Something went wrong. Please try again.',
  disclaimer = 'No spam, ever. Unsubscribe anytime.',
}: {
  /** Copy overrides (editable/translatable via the page manifests). */
  heading?: string;
  subheading?: string;
  emailLabel?: string;
  subscribeLabel?: string;
  subscribingLabel?: string;
  successMessage?: string;
  errorFallback?: string;
  disclaimer?: string;
}) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setError('');

    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        setStatus('error');
        setError(data.error || errorFallback);
        return;
      }

      setStatus('success');
      trackEvent('newsletter_signup');
      setEmail('');
    } catch {
      setStatus('error');
      setError(errorFallback);
    }
  };

  return (
    <section
      className="py-20 md:py-28 bg-[#F5F0E8]"
    >
      <div className="max-w-xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-sans text-3xl md:text-4xl font-light mb-3">
            {heading}
          </h2>
          <p className="text-base mb-8 text-[#5A5A6E]">
            {subheading}
          </p>

          {status === 'success' ? (
            <div className="flex items-center justify-center gap-2 text-[#9B7B3A]">
              <Check size={18} />
              <p className="text-sm font-medium">
                {successMessage}
              </p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3"
            >
              <label htmlFor="newsletter-email" className="sr-only">
                {emailLabel}
              </label>
              <input
                id="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={emailLabel}
                aria-label={emailLabel}
                disabled={status === 'loading'}
                className="flex-1 px-5 py-3 rounded-full text-sm outline-none transition-colors duration-300 placeholder:text-sm bg-white text-slate-900 placeholder:text-slate-400 border border-[#e0dac8] focus:border-[#9B7B3A] disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer bg-[#9B7B3A] text-white hover:bg-[#C4A95E] shadow-md hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Send size={14} />
                {status === 'loading' ? subscribingLabel : subscribeLabel}
              </button>
            </form>
          )}

          {status === 'error' && (
            <p className="text-xs mt-4 text-red-600">
              {error}
            </p>
          )}

          <p className="text-xs mt-4 text-[#8A8A9A]">
            {disclaimer}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
